import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail, STAGES, STAGE_LABELS } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Ship, ChevronRight, Plus, Search, CheckSquare, Square, Trash2, X, Clock, User, SlidersHorizontal } from "lucide-react";
import { StatusBadge } from "@/pages/Dashboard";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "active", label: "Active" },
  { key: "claim", label: "Claims" },
  { key: "completed", label: "Completed" },
];

function formatRelative(iso) {
  if (!iso) return "";
  const date = new Date(iso);
  const diff = Date.now() - date.getTime();
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return date.toLocaleDateString();
}

export default function ShipmentsList() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const buyerFilter = searchParams.get("buyer") || "";
  const stageFilter = searchParams.get("stage") || "";
  const fromFilter = searchParams.get("from") || "";
  const toFilter = searchParams.get("to") || "";
  const isAdmin = user?.role === "admin";
  const isOwner = !!user?.is_owner;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.get("/shipments");
      setItems(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const buyerLabel = useMemo(() => {
    if (!buyerFilter) return "";
    const match = items.find((s) => s.buyer_id === buyerFilter);
    return match?.buyer_name || "";
  }, [buyerFilter, items]);

  const buyerOptions = useMemo(() => {
    const map = new Map();
    items.forEach((s) => {
      if (s.buyer_id && !map.has(s.buyer_id)) map.set(s.buyer_id, s.buyer_name || s.buyer_id);
    });
    return Array.from(map, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [items]);

  const activeFilterCount =
    (buyerFilter ? 1 : 0) + (stageFilter ? 1 : 0) + (fromFilter ? 1 : 0) + (toFilter ? 1 : 0);

  const setParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value); else next.delete(key);
    setSearchParams(next, { replace: true });
  };

  const clearAllFilters = () => {
    const next = new URLSearchParams(searchParams);
    ["buyer", "stage", "from", "to"].forEach((k) => next.delete(k));
    setSearchParams(next, { replace: true });
  };

  const clearBuyerFilter = () => setParam("buyer", "");
  const clearStageFilter = () => setParam("stage", "");
  const clearDateFilter = () => { const next = new URLSearchParams(searchParams); next.delete("from"); next.delete("to"); setSearchParams(next, { replace: true }); };

  const filtered = useMemo(() => {
    let list = items;
    if (buyerFilter) list = list.filter((s) => s.buyer_id === buyerFilter);
    if (stageFilter) list = list.filter((s) => s.current_stage === stageFilter);
    if (fromFilter) {
      const fromTs = new Date(fromFilter).getTime();
      list = list.filter((s) => new Date(s.created_at).getTime() >= fromTs);
    }
    if (toFilter) {
      const toTs = new Date(toFilter).getTime() + 24 * 60 * 60 * 1000 - 1;
      list = list.filter((s) => new Date(s.created_at).getTime() <= toTs);
    }
    let byStatus;
    if (filter === "all") byStatus = list;
    else if (filter === "claim") byStatus = list.filter((s) => s.status === "claim" || s.has_open_claim);
    else byStatus = list.filter((s) => s.status === filter);
    const needle = q.trim().toLowerCase();
    if (!needle) return byStatus;
    return byStatus.filter((s) =>
      [s.reference, s.buyer_name, s.buyer_email, s.product, STAGE_LABELS[s.current_stage]]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(needle))
    );
  }, [items, filter, q, buyerFilter, stageFilter, fromFilter, toFilter]);

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const allSelectedInView = filtered.length > 0 && filtered.every((s) => selected.has(s.id));
  const toggleAll = () => {
    setSelected((prev) => {
      if (allSelectedInView) {
        const next = new Set(prev);
        filtered.forEach((s) => next.delete(s.id));
        return next;
      }
      const next = new Set(prev);
      filtered.forEach((s) => next.add(s.id));
      return next;
    });
  };

  const exitSelect = () => {
    setSelectMode(false);
    setSelected(new Set());
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Permanently delete ${selected.size} shipment${selected.size > 1 ? "s" : ""}? Linked documents, comments, and claims will also be removed.`)) return;
    setDeleting(true);
    try {
      const ids = Array.from(selected);
      const r = await api.post("/shipments/bulk-delete", { ids });
      toast.success(`Deleted ${r.data.deleted} shipment${r.data.deleted !== 1 ? "s" : ""}`);
      exitSelect();
      await reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally { setDeleting(false); }
  };

  return (
    <div className="p-4 sm:p-5 space-y-4">
      {/* Header */}
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-2">
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-[#1F1F1F]">Shipments</h1>
          <div className="flex items-center gap-1.5 shrink-0">
            <button
              onClick={() => setFiltersOpen(true)}
              className={`inline-flex items-center gap-1 text-xs font-semibold rounded-lg px-2.5 py-1.5 border transition-colors ${
                activeFilterCount > 0
                  ? "bg-[#F4E8E9] text-[#6B0F1A] border-[#6B0F1A]/30"
                  : "bg-white text-stone-700 border-stone-200 hover:border-[#6B0F1A] hover:text-[#6B0F1A]"
              }`}
              data-testid="open-filters-btn"
              aria-label="Filters"
            >
              <SlidersHorizontal className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Filters</span>
              {activeFilterCount > 0 && (
                <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-[#6B0F1A] text-white text-[10px] font-bold">
                  {activeFilterCount}
                </span>
              )}
            </button>
            {isOwner && !selectMode && items.length > 0 && (
              <button
                onClick={() => setSelectMode(true)}
                className="inline-flex items-center gap-1 bg-white border border-stone-200 hover:border-[#6B0F1A] text-stone-700 hover:text-[#6B0F1A] text-xs font-semibold rounded-lg px-2.5 py-1.5"
                data-testid="enter-select-mode"
                aria-label="Select multiple"
              >
                <CheckSquare className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Select</span>
              </button>
            )}
            {!selectMode && (
              <Link
                to="/app/shipments/new"
                className="inline-flex items-center gap-1 bg-[#6B0F1A] text-white text-xs font-semibold rounded-lg px-3 py-1.5 hover:bg-[#56121C] active:scale-[0.98]"
                data-testid="create-shipment-btn"
              >
                <Plus className="w-3.5 h-3.5" /> New
              </Link>
            )}
          </div>
        </div>

        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search reference, buyer, product…"
            className="w-full bg-white border border-stone-200 rounded-xl pl-9 pr-3 py-2.5 text-sm outline-none focus:border-[#A8323F] focus:ring-2 focus:ring-[#A8323F]/15"
            data-testid="shipments-search"
          />
        </div>

        <div className="flex gap-1.5 overflow-x-auto no-scrollbar -mx-1 px-1" data-testid="shipments-filters">
          {FILTERS.map((f) => {
            const count = f.key === "all"
              ? items.length
              : f.key === "claim"
                ? items.filter((s) => s.status === "claim" || s.has_open_claim).length
                : items.filter((s) => s.status === f.key).length;
            const active = filter === f.key;
            return (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                data-testid={`filter-${f.key}`}
                className={`whitespace-nowrap inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                  active
                    ? "bg-[#6B0F1A] text-white border-[#6B0F1A]"
                    : "bg-white text-stone-600 border-stone-200 hover:border-[#A8323F]"
                }`}
              >
                {f.label}
                <span className={`text-[10px] font-bold rounded-full px-1.5 py-px ${active ? "bg-white/20 text-white" : "bg-stone-100 text-stone-500"}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {activeFilterCount > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="active-filters">
          {buyerFilter && (
            <FilterChip label={`Buyer: ${buyerLabel || "Selected"}`} onClear={clearBuyerFilter} testId="clear-buyer-filter" />
          )}
          {stageFilter && (
            <FilterChip label={`Stage: ${STAGE_LABELS[stageFilter] || stageFilter}`} onClear={clearStageFilter} testId="clear-stage-filter" />
          )}
          {(fromFilter || toFilter) && (
            <FilterChip
              label={`Date: ${fromFilter || "any"} → ${toFilter || "any"}`}
              onClear={clearDateFilter}
              testId="clear-date-filter"
            />
          )}
          {activeFilterCount > 1 && (
            <button onClick={clearAllFilters} className="text-[11px] font-semibold text-stone-500 hover:text-[#6B0F1A] underline" data-testid="clear-all-filters">
              Clear all
            </button>
          )}
        </div>
      )}

      {/* Select-mode toolbar */}
      {selectMode && (
        <div className="bg-white border border-[#6B0F1A]/20 rounded-2xl p-3 flex items-center gap-3 sticky top-2 z-10 shadow-sm" data-testid="select-toolbar">
          <button onClick={toggleAll} className="text-xs font-semibold text-[#6B0F1A] inline-flex items-center gap-1.5" data-testid="toggle-select-all">
            {allSelectedInView ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
            {allSelectedInView ? "Unselect all" : "Select all"}
          </button>
          <span className="text-xs text-stone-500 flex-1">{selected.size} selected</span>
          <button
            onClick={bulkDelete}
            disabled={deleting || selected.size === 0}
            className="inline-flex items-center gap-1.5 bg-red-600 hover:bg-red-700 text-white text-xs font-semibold rounded-lg px-3 py-1.5 disabled:opacity-40"
            data-testid="bulk-delete-btn"
          >
            <Trash2 className="w-3.5 h-3.5" /> {deleting ? "Deleting…" : "Delete"}
          </button>
          <button onClick={exitSelect} className="text-stone-400 hover:text-stone-600" data-testid="exit-select-mode">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="space-y-2.5" data-testid="shipments-list">
        {loading && <div className="text-sm text-stone-400">Loading…</div>}
        {!loading && filtered.length === 0 && (
          <div className="bg-white border border-dashed border-stone-300 rounded-2xl p-8 text-center">
            <Ship className="w-8 h-8 mx-auto text-stone-300" />
            <div className="font-semibold text-[#1F1F1F] mt-3">No shipments found</div>
            <div className="text-sm text-stone-500 mt-1">Adjust filters or create a new shipment.</div>
          </div>
        )}
        {filtered.map((s) => (
          <ShipmentCard
            key={s.id}
            s={s}
            selectMode={selectMode}
            isSelected={selected.has(s.id)}
            onToggle={() => toggleOne(s.id)}
          />
        ))}
      </div>

      {filtersOpen && createPortal(
        <FilterPanelModal
          buyerFilter={buyerFilter}
          stageFilter={stageFilter}
          fromFilter={fromFilter}
          toFilter={toFilter}
          buyerOptions={buyerOptions}
          onApply={(next) => {
            const sp = new URLSearchParams(searchParams);
            ["buyer", "stage", "from", "to"].forEach((k) => {
              if (next[k]) sp.set(k, next[k]); else sp.delete(k);
            });
            setSearchParams(sp, { replace: true });
            setFiltersOpen(false);
          }}
          onClose={() => setFiltersOpen(false)}
        />,
        document.body
      )}
    </div>
  );
}

function FilterChip({ label, onClear, testId }) {
  return (
    <span className="inline-flex items-center gap-1.5 bg-[#F4E8E9] text-[#6B0F1A] border border-[#6B0F1A]/20 text-xs font-semibold rounded-full px-2.5 py-1">
      {label}
      <button onClick={onClear} className="hover:text-[#56121C]" data-testid={testId} aria-label="Clear filter">
        <X className="w-3 h-3" />
      </button>
    </span>
  );
}

function FilterPanelModal({ buyerFilter, stageFilter, fromFilter, toFilter, buyerOptions, onApply, onClose }) {
  const [draft, setDraft] = useState({
    buyer: buyerFilter,
    stage: stageFilter,
    from: fromFilter,
    to: toFilter,
  });
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const reset = () => setDraft({ buyer: "", stage: "", from: "", to: "" });

  return (
    <div
      className="fixed inset-0 z-[200] bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
      data-testid="filter-panel-modal"
    >
      <div
        className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 px-5 pt-5 pb-3 border-b border-stone-100">
          <div>
            <h3 className="font-display text-lg font-extrabold text-[#1F1F1F] flex items-center gap-2">
              <SlidersHorizontal className="w-4 h-4 text-[#6B0F1A]" /> Filters
            </h3>
            <p className="text-[11px] text-stone-500">Refine the shipments list.</p>
          </div>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-700" data-testid="close-filter-modal" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-y-auto px-5 py-4 space-y-4 flex-1">
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-stone-500 block mb-1.5">Buyer</label>
            <select
              value={draft.buyer}
              onChange={(e) => set("buyer", e.target.value)}
              className="w-full bg-white border border-stone-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-[#A8323F] focus:ring-2 focus:ring-[#A8323F]/15"
              data-testid="filter-buyer"
            >
              <option value="">All buyers</option>
              {buyerOptions.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-stone-500 block mb-1.5">Current stage</label>
            <select
              value={draft.stage}
              onChange={(e) => set("stage", e.target.value)}
              className="w-full bg-white border border-stone-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-[#A8323F] focus:ring-2 focus:ring-[#A8323F]/15"
              data-testid="filter-stage"
            >
              <option value="">All stages</option>
              {STAGES.map((st) => (
                <option key={st} value={st}>{STAGE_LABELS[st] || st}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-stone-500 block mb-1.5">Created date</label>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <span className="text-[10px] text-stone-400 uppercase">From</span>
                <input
                  type="date"
                  value={draft.from}
                  onChange={(e) => set("from", e.target.value)}
                  className="w-full bg-white border border-stone-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#A8323F] focus:ring-2 focus:ring-[#A8323F]/15"
                  data-testid="filter-from"
                />
              </div>
              <div>
                <span className="text-[10px] text-stone-400 uppercase">To</span>
                <input
                  type="date"
                  value={draft.to}
                  onChange={(e) => set("to", e.target.value)}
                  className="w-full bg-white border border-stone-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#A8323F] focus:ring-2 focus:ring-[#A8323F]/15"
                  data-testid="filter-to"
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {[
                { label: "Last 7 days", days: 7 },
                { label: "Last 30 days", days: 30 },
                { label: "Last 90 days", days: 90 },
              ].map((p) => (
                <button
                  key={p.days}
                  type="button"
                  onClick={() => {
                    const to = new Date();
                    const from = new Date(); from.setDate(from.getDate() - p.days);
                    const fmt = (d) => d.toISOString().slice(0, 10);
                    setDraft((d) => ({ ...d, from: fmt(from), to: fmt(to) }));
                  }}
                  className="text-[10px] font-semibold text-stone-600 bg-stone-100 hover:bg-stone-200 rounded-full px-2 py-1"
                  data-testid={`preset-${p.days}d`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 px-5 py-3 border-t border-stone-100 bg-stone-50 rounded-b-2xl sm:rounded-b-2xl">
          <button
            onClick={reset}
            className="text-xs font-semibold text-stone-600 hover:text-[#6B0F1A] underline"
            data-testid="reset-filters"
          >
            Reset
          </button>
          <div className="flex-1" />
          <button
            onClick={onClose}
            className="text-xs font-semibold text-stone-600 hover:text-stone-800 px-3 py-2"
            data-testid="cancel-filter"
          >
            Cancel
          </button>
          <button
            onClick={() => onApply(draft)}
            className="bg-[#6B0F1A] hover:bg-[#56121C] text-white text-xs font-semibold rounded-lg px-4 py-2"
            data-testid="apply-filters"
          >
            Apply filters
          </button>
        </div>
      </div>
    </div>
  );
}

function ShipmentCard({ s, selectMode, isSelected, onToggle }) {
  const stageIdx = STAGES.indexOf(s.current_stage);
  const totalStages = STAGES.length;
  const progressPct = stageIdx >= 0 ? Math.round(((stageIdx + 1) / totalStages) * 100) : 0;
  const stageLabel = STAGE_LABELS[s.current_stage] || s.current_stage;
  const updatedAtIso = s.updated_at || s.created_at;
  const updatedRel = formatRelative(updatedAtIso);
  const updatedFull = updatedAtIso ? new Date(updatedAtIso).toLocaleString() : "";
  const lastActor = s.last_modified_by_name || s.created_by_name;

  const cardClasses = `block bg-white border rounded-2xl p-3.5 transition-all ${
    selectMode
      ? isSelected
        ? "border-[#6B0F1A] bg-[#F4E8E9]/30 cursor-pointer"
        : "border-stone-200 hover:border-[#A8323F] cursor-pointer"
      : "border-stone-200 hover:border-[#A8323F] hover:shadow-sm active:scale-[0.997]"
  }`;

  const body = (
    <div className="flex gap-3 items-start">
      {selectMode ? (
        <div className="pt-0.5 shrink-0">
          {isSelected ? <CheckSquare className="w-5 h-5 text-[#6B0F1A]" /> : <Square className="w-5 h-5 text-stone-300" />}
        </div>
      ) : (
        <div className="w-9 h-9 rounded-xl bg-[#F4E8E9] text-[#6B0F1A] flex items-center justify-center shrink-0 mt-0.5">
          <Ship className="w-4 h-4" />
        </div>
      )}

      <div className="flex-1 min-w-0">
        {/* Top row: buyer + status */}
        <div className="flex items-start gap-2 justify-between">
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-[15px] text-[#1F1F1F] truncate leading-tight">{s.buyer_name}</div>
            <div className="text-[11px] text-stone-400 font-mono truncate mt-0.5">{s.reference}</div>
          </div>
          <StatusBadge status={s.status} />
        </div>

        {/* Product line */}
        {s.product && (
          <div className="text-xs text-stone-600 mt-1.5 truncate">{s.product}{s.variety ? ` · ${s.variety}` : ""}</div>
        )}

        {/* Stage chip + progress */}
        <div className="mt-2 flex items-center gap-2">
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-[#F4E8E9] text-[#6B0F1A] rounded-full px-2 py-0.5 shrink-0">
            {stageIdx + 1}/{totalStages} · {stageLabel}
          </span>
          <div className="flex-1 h-1 bg-stone-100 rounded-full overflow-hidden min-w-[40px]">
            <div className="h-full bg-[#6B0F1A] transition-all" style={{ width: `${progressPct}%` }} />
          </div>
        </div>

        {/* Footer: meta */}
        {(lastActor || updatedRel) && (
          <div className="mt-2 flex items-center gap-3 text-[10px] text-stone-400" title={updatedFull}>
            {lastActor && (
              <span className="inline-flex items-center gap-1 truncate">
                <User className="w-2.5 h-2.5" />
                <span className="truncate">{lastActor}</span>
              </span>
            )}
            {updatedRel && (
              <span className="inline-flex items-center gap-1 shrink-0">
                <Clock className="w-2.5 h-2.5" />
                {updatedRel}
              </span>
            )}
          </div>
        )}
      </div>

      {!selectMode && <ChevronRight className="w-4 h-4 text-stone-300 shrink-0 mt-1" />}
    </div>
  );

  return selectMode ? (
    <div
      data-testid={`shipment-card-${s.id}`}
      data-selected={isSelected || undefined}
      onClick={onToggle}
      className={cardClasses}
    >
      {body}
    </div>
  ) : (
    <Link
      to={`/app/shipments/${s.id}`}
      data-testid={`shipment-card-${s.id}`}
      className={cardClasses}
    >
      {body}
    </Link>
  );
}
